import { AnalyticsProperties, parseLineLinkPayload } from '@play-co/gcinstant';
import type { AnalyticsPayload } from '@play-co/replicant';
import { localStorage } from '@play-co/browser-storage';
import { duration } from 'src/replicant/utils/duration';
import { EntryProperties } from './analytics/properties';
import { getApplication, addAppVisibilityListener, delay } from './utils';
import StateObserver from 'src/StateObserver';
import {
  forceGameUpdateAndReentry,
  subscribeToLinks,
} from './native/native.util';
import { captureGenericError } from './sentry';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import sounds from './sounds';
import { showLoading } from 'src/state/ui';

export const PN_STORAGE_KEY = 'pn_payload_cache';
export const LINK_STORAGE_KEY = 'link_payload_cache';
const HOUR_IN_MS = duration({ hours: 1 });

let gameHiddenTimestamp = 0;
const oneHourHasPassed = () =>
  gameHiddenTimestamp > 0 && Date.now() - gameHiddenTimestamp > HOUR_IN_MS;

function getSessionResumeEntryData() {
  const entryData: Partial<AnalyticsProperties.EntryData> = {};

  // merge the cached PN payload if it exists
  let payload = localStorage.getItem(PN_STORAGE_KEY);
  localStorage.removeItem(PN_STORAGE_KEY);

  if (payload) {
    try {
      Object.assign(entryData, JSON.parse(payload));
    } catch (err) {
      captureGenericError(
        `Error augmenting entry data with cached PN payload`,
        err,
      );
    }
  }

  // merge the cached link payload if it exists
  payload = localStorage.getItem(LINK_STORAGE_KEY);
  localStorage.removeItem(LINK_STORAGE_KEY);

  if (payload) {
    try {
      Object.assign(entryData, JSON.parse(payload));
    } catch (err) {
      captureGenericError(
        `Error augmenting entry data with cached link payload`,
        err,
      );
    }
  }

  return entryData;
}

export async function initializeSessionResume() {
  gameHiddenTimestamp = Date.now();
  const entryData = getSessionResumeEntryData();

  addAppVisibilityListener(handleAppState);
  subscribeToLinks(handleLink);

  return entryData;
}

// We want to make sure that native players take game updates in a timely manner,
// so restart the app if it's been in the background for over an hour
function handleAppState(appState: DocumentVisibilityState) {
  if (appState === 'hidden') {
    gameHiddenTimestamp = Date.now();
  }
  if (appState === 'visible' && oneHourHasPassed()) {
    // Show splash screen while the app restarts.
    showFakeSplash();
    // Give up to 500ms in case there's a link or notification to handle.
    // This is to avoid reloading before the deeplink or notification is handled.
    void delay(500).then(() => forceGameUpdateAndReentry());
  }
}

function handleLink(url: string) {
  const entryData = parseLineLinkPayload(url);

  // Should set the app was in background flag to true.
  const entryProps = entryData as unknown as EntryProperties;
  if (entryProps?.crossplayTracking) {
    entryProps.crossplayTracking.wasInBackground = true;
  }

  restartWithPayload(LINK_STORAGE_KEY, entryData);
}

export async function restartWithPayload(
  cacheKey: string,
  payload: AnalyticsPayload | undefined,
) {
  showFakeSplash();

  if (payload) {
    localStorage.setItem(cacheKey, JSON.stringify(payload));
  }
  await forceGameUpdateAndReentry();
}

let fakeSplashShown = false;
function showFakeSplash() {
  // Only show the fake splash once.
  if (fakeSplashShown) {
    return;
  }
  fakeSplashShown = true;

  // Show loading spinner, in case the splash fails to show.
  StateObserver.dispatch(showLoading());

  // Mute all sounds.
  sounds.musicMuted = true;
  sounds.sfxMuted = true;

  // Fade in a fake splash screen while the app restarts.
  const rootView = getApplication().getRootView();
  if (rootView) {
    const { width, height: rootHeihgt } = rootView.style;
    let { height } = StateObserver.getState().ui.screenSize;
    const y = -Math.max(height - rootHeihgt, 0) / 2;
    new ImageScaleView({
      superview: rootView,
      scaleMethod: 'cover',
      width,
      height,
      y,
      infinite: true,
      image: 'assets/splash/splash_test_c.png',
      zIndex: Number.MAX_SAFE_INTEGER,
    });
  }
}
